import type { ButtonHTMLAttributes, ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'text'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  readonly fullWidth?: boolean
  readonly isLoading?: boolean
  readonly leftIcon?: ReactNode
  readonly rightIcon?: ReactNode
  readonly children: ReactNode
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // Per Kiran 2026-05-15: every button shares the deep-bamboo brand
  // colour (--color-primary). Primary + secondary are visually the
  // same fill now; ghost + text inherit the same bamboo border/text.
  primary: 'bg-forest text-white hover:opacity-90 active:opacity-80',
  secondary: 'bg-forest text-white hover:opacity-90 active:opacity-80',
  ghost: 'bg-transparent text-forest border border-forest hover:bg-parchment',
  text: 'bg-transparent text-forest underline-offset-2 hover:underline'
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-body-sm',
  md: 'px-5 py-2.5 text-body',
  lg: 'px-7 py-3 text-body-lg'
}

/**
 * Brand-aligned button. Per SOP 13:
 *   - primary  → terracotta pill (CTA)
 *   - secondary → forest pill (navigation actions)
 *   - ghost    → transparent w/ forest border
 *   - text     → underlined forest text
 * Always pill-shaped (rounded-full) except `text` variant.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  const isPill = variant !== 'text'
  const classes = [
    'inline-flex items-center justify-center gap-2 font-body font-medium transition',
    isPill ? 'rounded-full' : '',
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    fullWidth ? 'w-full' : '',
    disabled || isLoading ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button type={type} className={classes} disabled={disabled || isLoading} {...rest}>
      {isLoading ? <span aria-hidden className="inline-block animate-spin">◌</span> : leftIcon}
      <span>{children}</span>
      {!isLoading && rightIcon !== undefined ? rightIcon : null}
    </button>
  )
}
